import PropTypes from 'prop-types'
import styled from '@nobia/zeus-components/lib/styled'
import React, { Suspense, useEffect, useState, useContext } from 'react'
import settings from '@nobia/zeus-components/lib/settings'

import Loader from '../../../components/loader/Loader'
import { data, initSelection } from '../../../brands/data'
import { getPrice, checkHideFlatPack } from '../../../components/cards/utils'
import { CABINET_TYPES } from '../../../components/cards/constants'
import { CONFIGURATOR_STATE_KEY } from '../../../components/login'

import Section from '../../../components/section/Section'
import Context from '../../../app/context'
import {
  trackOptionSelect,
  trackStorageOptionSelect,
} from '../../../utils/tracking'

const Wrapper = styled.div`
  background-color: ${(p) => p.theme.section.bgColor};
`

const getSections = (configuration, selection) =>
  configuration.map((section) => ({
    ...section,
    cards: section?.cards?.map((card) => ({
      ...card,
      id: section.id,
      selected: selection[section.id].title === card.title,
    })),
  }))

export const getFilteredWorktops = (sections, worktops) => {
  const filterSections = sections.map((s) => {
    if (s.id === 'worktops' && worktops.length !== 0) {
      const cards = s.cards.filter((c) => worktops.some((w) => w === c.title))
      return { ...s, cards }
    }
    return s
  })
  return filterSections
}

const getSavedState = () => {
  if (settings.brand !== 'magnet') return null
  try {
    const raw = localStorage.getItem(CONFIGURATOR_STATE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const Configurator = ({
  handleSummary,
  summary,
  months,
  handleMonths,
  setShowModal,
  financeOption,
  financeId,
  financeTitle,
  handleFinanceOption,
  monthsToPayOptions,
}) => {
  const savedState = getSavedState()
  const [selection, setSelection] = useState(savedState?.selection ?? initSelection)
  const [filterWorktops, setFilterWorktops] = useState(
    savedState?.sections
      ? (savedState.sections.find(s => s.id === 'range')?.cards?.find(c => c.selected)?.filterWorktops || data[0].cards[0].filterWorktops || [])
      : (data[0].cards[0].filterWorktops || [])
  )

  const { setSelectionContext, storage } = useContext(Context)

  const sections = getSections(data, selection)

  const getNewSelection = (card, filterWtops) => {
    let newSelection
    if (card.id === 'range' && selection.worktops.title !== filterWtops[0]) {
      const filteredSections = getFilteredWorktops(sections, filterWtops)
      const worktops = filteredSections.find((s) => s.id === 'worktops').cards
      const currentWorktopIndex = worktops.findIndex((x) =>
        selection.worktops.title.includes(x.title)
      )
      const selectedWorktop =
        worktops[
        filterWtops.length > 1 && currentWorktopIndex !== -1
          ? currentWorktopIndex
          : 0
        ]
      newSelection = {
        ...selection,
        [card.id]: card,
        worktops: selectedWorktop,
      }
    } else {
      newSelection = {
        ...selection,
        [card.id]: card,
      }
    }

    // Auto-set pre-built when flat-pack should be hidden (only for brands with cabinet)
    const cabinetSection = sections.find((s) => s.id === 'cabinet')

    if (cabinetSection && newSelection?.cabinet) {
      const isHideFlatPack = checkHideFlatPack(newSelection)
      const isFlatPack = newSelection.cabinet.type === CABINET_TYPES.FLAT_PACK

      if (isHideFlatPack && isFlatPack) {
        const preBuiltCard = cabinetSection.cards.find((c) => c.type === CABINET_TYPES.PRE_BUILT)

        if (preBuiltCard) {
          newSelection = {
            ...newSelection,
            cabinet: {
              ...preBuiltCard,
              id: 'cabinet',
            },
          }
        }
      }
    }

    return newSelection
  }

  const handleSelect = (card, scroll) => {
    if (card.title === selection[card.id]) return
    const filterWtops = card?.filterWorktops || filterWorktops
    setFilterWorktops(filterWtops)
    const newSelection = getNewSelection(card, filterWtops)
    const index = sections.findIndex((section) => section.id === card.id)
    if (scroll) {
      const nextSection = document.getElementById(sections[index + 1].id)
      const { height } = nextSection.getBoundingClientRect()
      window.scroll({
        top: nextSection.offsetTop - height / 2,
        left: 0,
        behavior: 'smooth',
      })
    }

    // Skip init load tracking
    if (typeof scroll !== 'undefined') {
      if (card.type === 'storage') {
        const options = storage.cards
          .filter((c) => c.amount)
          .map((c) => ({ storage_option: c.title, quantity: c.amount }))
        trackStorageOptionSelect({
          optionIndex: index,
          options,
        })
      } else {
        const value = card.option?.value
        trackOptionSelect({
          optionIndex: index,
          optionName: card.title,
          optionValue: value,
        })
      }
    }
    setSelection(newSelection)
    setSelectionContext(newSelection)
  }

  useEffect(() => {
    const sum = Object.values(selection)
      .map((value) => getPrice(value, selection, storage))
      .reduce((a, b) => a + b, 0)
    handleSummary(sum)
  }, [handleSummary, selection, storage])

  useEffect(() => {
    if (settings.brand === 'magnet') {
      localStorage.removeItem(CONFIGURATOR_STATE_KEY)
    }
    if (!savedState) {
      handleSelect(sections.find((s) => s.id === 'range').cards[0])
    }
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 800)
  }, [])

  const filteredSections = getFilteredWorktops(sections, filterWorktops)

  return (
    <Wrapper>
      {filteredSections.map((section) => (
        <Suspense key={section.title} fallback={<Loader />}>
          <Section
            {...section}
            onSelect={handleSelect}
            sections={filteredSections}
            selection={selection}
            summary={summary}
            months={months}
            monthsToPayOptions={monthsToPayOptions}
            handleMonths={handleMonths}
            setShowModal={setShowModal}
            financeOption={financeOption}
            financeId={financeId}
            financeTitle={financeTitle}
            handleFinanceOption={handleFinanceOption}
          />
        </Suspense>
      ))}
    </Wrapper>
  )
}

Configurator.propTypes = {
  handleSummary: PropTypes.func.isRequired,
  summary: PropTypes.number.isRequired,
  months: PropTypes.shape({
    months: PropTypes.number.isRequired,
  }).isRequired,
  financeOption: PropTypes.shape({}).isRequired,
  financeId: PropTypes.string.isRequired,
  financeTitle: PropTypes.string.isRequired,
  setShowModal: PropTypes.func.isRequired,
  handleFinanceOption: PropTypes.func.isRequired,
  handleMonths: PropTypes.func.isRequired,
  monthsToPayOptions: PropTypes.arrayOf(
    PropTypes.shape({
      repaymentFactor: PropTypes.number.isRequired,
      months: PropTypes.number.isRequired,
    })
  ).isRequired,
}

export default Configurator
